/**
 * Generic Export Modal Component
 * 
 * Reusable component for exporting data from any collection.
 * Supports field selection, filtering, and multiple export formats.
 * 
 * Usage:
 * <ExportModal
 *   isOpen={showExportModal}
 *   onClose={() => setShowExportModal(false)}
 *   collection="companies"
 *   selectedItems={selectedCompanies}
 *   onExport={handleExport}
 * />
 */

import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../utils/config';
import './ExportModal.scss';

interface ExportModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Collection name (companies, users, requests, suppliers) */
  collection: string;
  /** Selected items for filtering (optional) */
  selectedItems?: any[];
  /** Export callback */
  onExport?: (exportData: ExportData) => void;
  /** Collection display name */
  collectionDisplayName?: string;
  /** Custom fields to show instead of backend fields */
  customFields?: Field[];
}

interface Field {
  name: string;
  type: string;
  description: string;
}

interface ExportData {
  collection: string;
  fields: string[];
  filters: any;
  filename: string;
  format: 'excel' | 'csv';
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  collection,
  selectedItems = [],
  onExport,
  collectionDisplayName,
  customFields
}) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<'excel' | 'csv'>('excel');
  const [filename, setFilename] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Load available fields when modal opens
  useEffect(() => {
    if (isOpen && collection) {
      if (customFields) {
        // Use custom fields if provided
        setFields(customFields);
        setSelectedFields(customFields.map(field => field.name));
        setFilename(`${collection}_export`);
      } else {
        // Otherwise load from backend
        loadAvailableFields();
        setFilename(`${collection}_export`);
      }
    }
  }, [isOpen, collection, customFields]);

  const loadAvailableFields = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.EXPORT_FIELDS(collection)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load fields');
      }

      const result = await response.json();
      if (result.success) {
        setFields(result.fields);
        // Select all fields by default
        setSelectedFields(result.fields.map((field: Field) => field.name));
      } else {
        setError(result.message || 'Failed to load fields');
      }
    } catch (error) {
      console.error('Error loading fields:', error);
      setError('Failed to load available fields');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldToggle = (fieldName: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldName) 
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(fields.map(field => field.name));
  };

  const handleSelectNone = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const exportData: ExportData = {
        collection,
        fields: selectedFields,
        filters: selectedItems.length > 0 ? { _id: { $in: selectedItems.map(item => item.id || item._id) } } : {},
        filename: filename.trim(),
        format: exportFormat
      };

      // Call parent export handler if provided
      if (onExport) {
        console.log('🚀 ExportModal calling parent onExport handler');
        await onExport(exportData);
      } else {
        console.log('🚀 ExportModal using default performExport');
        // Default export behavior
        await performExport(exportData);
      }

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      setError('Export failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const performExport = async (exportData: ExportData) => {
    console.log('🚀 Export starting with data:', exportData);
    console.log('🚀 Export URL:', API_ENDPOINTS.EXPORT_DATA);
    
    const token = localStorage.getItem('auth_token');
    console.log('🚀 Token available:', !!token);
    
    const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(exportData)
    });

    console.log('🚀 Export response status:', response.status);
    console.log('🚀 Export response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚀 Export error response:', errorText);
      throw new Error(`Export failed: ${response.status} ${errorText}`);
    }

    // Handle file download
    const blob = await response.blob();
    console.log('🚀 Export blob size:', blob.size);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Format: isim_YYYYMMDD
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const baseFilename = exportData.filename || collection;
    a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleClose = () => {
    setError('');
    setSelectedFields([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <h2 className="export-modal-title">
            Export {collectionDisplayName || collection}
          </h2>
          <button 
            className="export-modal-close"
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="export-modal-content">
          {isLoading && fields.length === 0 ? (
            <div className="export-modal-loading">
              <p>Loading available fields...</p>
            </div>
          ) : (
            <>
              {/* Filename Input */}
              <div className="export-field-group">
                <label className="export-field-label">
                  Filename
                </label>
                <input
                  type="text"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  className="export-field-input"
                  placeholder="Enter filename"
                  disabled={isLoading}
                />
              </div>

              {/* Export Format */}
              <div className="export-field-group">
                <label className="export-field-label">
                  Export Format
                </label>
                <div className="export-format-options">
                  <label className="export-format-option">
                    <input
                      type="radio"
                      name="format"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={(e) => setExportFormat(e.target.value as 'excel')}
                      disabled={isLoading}
                    />
                    Excel (.xlsx)
                  </label>
                  <label className="export-format-option">
                    <input
                      type="radio"
                      name="format"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value as 'csv')}
                      disabled={isLoading}
                    />
                    CSV (.csv)
                  </label>
                </div>
              </div>

              {/* Field Selection */}
              <div className="export-field-group">
                <div className="export-field-header">
                  <label className="export-field-label">
                    Select Fields to Export ({selectedFields.length}/{fields.length})
                  </label>
                  <div className="export-field-actions">
                    <button
                      type="button"
                      className="export-field-action-btn"
                      onClick={handleSelectAll}
                      disabled={isLoading}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="export-field-action-btn"
                      onClick={handleSelectNone}
                      disabled={isLoading}
                    >
                      Select None
                    </button>
                  </div>
                </div>
                
                {fields.length === 0 ? (
                  <div className="export-field-empty">
                    <p>No fields available for export. Please check if the collection is supported.</p>
                  </div>
                ) : (
                  <div className="export-fields-grid">
                    {fields.map((field) => (
                      <div key={field.name} className="export-field-item">
                        <label className="export-field-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.name)}
                            onChange={() => handleFieldToggle(field.name)}
                            disabled={isLoading}
                          />
                          <span className="export-field-name">{field.name}</span>
                          <span className="export-field-type">({field.type})</span>
                        </label>
                        <p className="export-field-description">{field.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Items Info */}
              {selectedItems.length > 0 && (
                <div className="export-selection-info">
                  <p>Exporting {selectedItems.length} selected {collectionDisplayName || collection}</p>
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="export-modal-error">
            {error}
          </div>
        )}

        <div className="export-modal-actions">
          <button
            type="button"
            className="export-modal-cancel"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="export-modal-export"
            onClick={handleExport}
            disabled={isLoading || selectedFields.length === 0}
          >
            {isLoading ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
